<template>
  <div class="state-overrides-section">
    <CRow class="form-group">
      <CCol
        sm="3"
        class="text-right pr-3"
      >
        <strong>State Overrides</strong>
      </CCol>
      <CCol sm="9">
        <p class="text-muted mb-3">
          Set different margins for specific US states (optional)
        </p>
      </CCol>
    </CRow>

    <!-- Add State Dropdown -->
    <CRow class="form-group">
      <CCol sm="3" />
      <CCol sm="9">
        <div class="d-flex align-items-center">
          <CSelect
            :value.sync="selectedStateForAdd"
            :options="availableStatesForDropdown"
            placeholder="Search and select a state..."
            class="mr-2 mb-0"
            style="max-width: 300px;"
          />

          <CButton
            color="success"
            :disabled="!selectedStateForAdd"
            @click="addStateFromDropdown"
          >
            Add Override
          </CButton>
        </div>
      </CCol>
    </CRow>

    <!-- Map and Sidebar -->
    <CRow class="form-group">
      <CCol sm="3" />
      <CCol sm="9">
        <CRow>
          <!-- Map -->
          <CCol md="8">
            <div class="map-container">
              <USStateMap
                :state-overrides="stateOverrides"
                :default-margins="defaultMargins"
                @state-click="handleStateClick"
              />
              <div class="mt-2 text-muted small">
                <span class="color-legend">
                  <span class="legend-item">
                    <span
                      class="legend-color"
                      style="background: #e5e7eb;"
                    /> No Override
                  </span>
                  <span class="legend-item">
                    <span
                      class="legend-color"
                      style="background: #4ade80;"
                    /> Lower
                  </span>
                  <span class="legend-item">
                    <span
                      class="legend-color"
                      style="background: #fbbf24;"
                    /> Similar
                  </span>
                  <span class="legend-item">
                    <span
                      class="legend-color"
                      style="background: #f87171;"
                    /> Higher
                  </span>
                </span>
              </div>
            </div>
          </CCol>

          <!-- Sidebar -->
          <CCol md="4">
            <div class="overrides-sidebar">
              <h6 class="mb-3">
                Active Overrides ({{ stateOverrides.length }})
              </h6>

              <div
                v-if="stateOverrides.length > 0"
                class="override-list"
              >
                <CCard
                  v-for="override in stateOverrides"
                  :key="override.stateCode"
                  class="mb-2"
                >
                  <CCardBody class="p-2">
                    <div class="d-flex justify-content-between align-items-start">
                      <div>
                        <strong>{{ getStateName(override.stateCode) }}</strong><br>
                        <small class="text-muted">
                          Min: {{ override.minMarginPercent }}% | Max: {{ override.maxMarginPercent }}%
                        </small>
                      </div>
                      <div class="d-flex flex-column">
                        <CButton
                          color="info"
                          size="sm"
                          class="mb-1"
                          @click="editStateOverride(override.stateCode)"
                        >
                          Edit
                        </CButton>
                        <ButtonLoading
                          color="danger"
                          size="sm"
                          :loading="removingStateCode === override.stateCode"
                          @click="removeStateOverride(override.stateCode)"
                        >
                          Remove
                        </ButtonLoading>
                      </div>
                    </div>
                  </CCardBody>
                </CCard>
              </div>

              <div
                v-else
                class="text-muted text-center py-4"
              >
                No state overrides configured.<br>
                Click a state on the map or use the dropdown above.
              </div>
            </div>
          </CCol>
        </CRow>
      </CCol>
    </CRow>

    <!-- State Edit Modal -->
    <CModal
      title="State Margin Override"
      :show.sync="showStateEditModal"
      @update:show="closeStateEditModal"
    >
      <CForm>
        <CInput
          :value="getStateName(stateEditForm.stateCode)"
          label="State"
          horizontal
          readonly
          disabled
        />

        <CInput
          v-model="$v.stateEditForm.minMarginPercent.$model"
          type="number"
          label="Min Margin %"
          horizontal
          placeholder="5"
          :is-valid="checkIfValid(['stateEditForm', 'minMarginPercent'])"
          invalid-feedback="Min must be 5-100%"
        />

        <CInput
          v-model="$v.stateEditForm.maxMarginPercent.$model"
          type="number"
          label="Max Margin %"
          horizontal
          placeholder="5"
          :is-valid="checkIfValid(['stateEditForm', 'maxMarginPercent'])"
          :invalid-feedback="maxMarginInvalidFeedback"
        />
      </CForm>

      <template #footer>
        <ButtonLoading
          color="primary"
          :loading="saving"
          @click="saveStateOverride"
        >
          Save
        </ButtonLoading>
        <CButton
          color="secondary"
          :disabled="saving"
          @click="closeStateEditModal"
        >
          Cancel
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import { required, between } from 'vuelidate/lib/validators'
import formGenericMixin from '@/mixins/form-generic-mixin'
import USStateMap from '@/components/USStateMap.vue'
import ButtonLoading from '@/components/common/ButtonLoading.vue'
import { MARGIN_PERCENT_MINIMUM } from '@/store/modules/bidmodifiers.store'

export default {
  name: 'StateOverridesSection',

  components: {
    USStateMap,
    ButtonLoading
  },

  mixins: [formGenericMixin],

  props: {
    stateOverrides: {
      type: Array,
      default: () => []
    },
    defaultMargins: {
      type: Object,
      required: true
    },
    usStates: {
      type: Array,
      default: () => []
    },
    saving: {
      type: Boolean,
      default: false
    },
    removingStateCode: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      showStateEditModal: false,
      stateEditForm: {
        stateCode: '',
        minMarginPercent: MARGIN_PERCENT_MINIMUM,
        maxMarginPercent: MARGIN_PERCENT_MINIMUM,
        isNew: true
      },
      selectedStateForAdd: ''
    }
  },

  validations() {
    return {
      stateEditForm: {
        stateCode: { required },
        minMarginPercent: {
          required,
          between: between(MARGIN_PERCENT_MINIMUM, 100)
        },
        maxMarginPercent: {
          required,
          between: between(this.stateEditForm?.minMarginPercent != null ? parseInt(this.stateEditForm?.minMarginPercent) : MARGIN_PERCENT_MINIMUM, 100)
        }
      }
    }
  },

  computed: {
    maxMarginInvalidFeedback() {
      const minVal = parseFloat(this.stateEditForm?.minMarginPercent) || MARGIN_PERCENT_MINIMUM
      return `Max margin must be at least ${minVal}% (min margin value)`
    },

    stateOptions() {
      if (!this.usStates || !Array.isArray(this.usStates)) {
        return []
      }

      return this.usStates.map(state => ({
        value: state.abbreviation,
        label: `${state.name} (${state.abbreviation})`
      }))
    },

    availableStatesForDropdown() {
      if (!this.stateOverrides || !Array.isArray(this.stateOverrides)) {
        return this.stateOptions
      }

      const usedCodes = this.stateOverrides.map(o => o.stateCode)
      return this.stateOptions.filter(s => !usedCodes.includes(s.value))
    }
  },

  watch: {
    saving(newVal, oldVal) {
      if (oldVal === true && newVal === false && this.showStateEditModal) {
        this.closeStateEditModal()
      }
    }
  },

  methods: {
    handleStateClick(stateCode) {
      const existing = this.stateOverrides && Array.isArray(this.stateOverrides)
        ? this.stateOverrides.find(o => o.stateCode === stateCode)
        : null

      if (existing) {
        this.stateEditForm = {
          stateCode: existing.stateCode,
          minMarginPercent: existing.minMarginPercent,
          maxMarginPercent: existing.maxMarginPercent,
          isNew: false
        }
      } else {
        this.stateEditForm = {
          stateCode,
          minMarginPercent: this.defaultMargins.min,
          maxMarginPercent: this.defaultMargins.max,
          isNew: true
        }
      }

      this.showStateEditModal = true
    },

    addStateFromDropdown() {
      if (!this.selectedStateForAdd) {
        return
      }

      this.stateEditForm = {
        stateCode: this.selectedStateForAdd,
        minMarginPercent: this.defaultMargins.min,
        maxMarginPercent: this.defaultMargins.max,
        isNew: true
      }

      this.showStateEditModal = true
    },

    saveStateOverride() {
      this.submitted = true
      this.$v.stateEditForm.$touch()

      if (this.$v.stateEditForm.$invalid) {
        return
      }

      const newOverride = {
        stateCode: this.stateEditForm.stateCode,
        minMarginPercent: parseFloat(this.stateEditForm.minMarginPercent),
        maxMarginPercent: parseFloat(this.stateEditForm.maxMarginPercent)
      }

      const currentOverrides = this.stateOverrides || []
      
      let updatedOverrides
      if (this.stateEditForm.isNew) {
        updatedOverrides = [...currentOverrides, newOverride]
      } else {
        updatedOverrides = currentOverrides.map(override => {
          return override.stateCode === newOverride.stateCode ? newOverride : override
        })
      }

      this.$emit('update:state-overrides', updatedOverrides)
    },

    closeStateEditModal() {
      this.showStateEditModal = false
      this.selectedStateForAdd = ''
      this.submitted = false
      this.$v.stateEditForm.$reset()
    },

    removeStateOverride(stateCode) {
      const currentOverrides = this.stateOverrides || []
      const updatedOverrides = currentOverrides.filter(override => override.stateCode !== stateCode)
      this.$emit('remove:state-override', { stateCode, updatedOverrides })
    },

    editStateOverride(stateCode) {
      this.handleStateClick(stateCode)
    },

    getStateName(stateCode) {
      if (!this.usStates || !Array.isArray(this.usStates)) {
        return stateCode
      }

      const state = this.usStates.find(s => s.abbreviation === stateCode)
      return state ? `${state.name} (${stateCode})` : stateCode
    }
  }
}
</script>

<style scoped>
.map-container {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  background: #f9fafb;
}

.overrides-sidebar {
  max-height: 500px;
  overflow-y: auto;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
}

.override-list {
  max-height: 420px;
  overflow-y: auto;
}

.color-legend {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.legend-color {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1px solid #9ca3af;
}
</style>
